import { showErrorMessage, showSuccessMessage } from '@/shared/components/toast-system';

export const stripMarkupTagsFromText = (text) => {
  return text.replace(/<[^>]*>/g, '').trim()
}

/**
 * Convert HTML content to plain text, preserving block-level line breaks.
 * Block elements (p, br, div, headings, li) are replaced with newlines
 * and consecutive blank lines are collapsed.
 */
export const htmlToPlainText = (html) => {
  if (!html) return '';
  const div = document.createElement('div');
  div.innerHTML = html;
  div.querySelectorAll('p, br, div, h1, h2, h3, h4, h5, h6, li').forEach((el) => {
    el.prepend(document.createTextNode('\n'));
  });
  return (div.textContent || div.innerText || '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

export const decodeHtmlEntities = (str) => {
  return str
    .replace(/&#(\d+);/g, function (match, num) {
      return String.fromCharCode(num)
    })
    .replace(/&([a-z]+);/gi, function (match, entity) {
      var entities = { quot: '"', apos: "'", amp: '&', lt: '<', gt: '>',
        nbsp: ' ', copy: '©', reg: '®',
      }
      return entities[entity] || match
    })
}

export function copyToClipboard(htmlContent, successMessage='Copied to clipboard', errorMessage='Could not copy to clipboard') {
    try {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = htmlContent;
      const plainText = tempDiv.textContent || tempDiv.innerText || '';
      
      const clipboardItem = new ClipboardItem({
        'text/plain': new Blob([plainText], { type: 'text/plain' }),
        'text/html': new Blob([htmlContent], { type: 'text/html' })
      });
      
      navigator.clipboard.write([clipboardItem])
        .then(() => {
          showSuccessMessage(successMessage);
        })
        .catch(err => {
          console.error('Failed to copy rich content, falling back to plain text: ', err);
          navigator.clipboard.writeText(plainText)
            .then(() => {
              showSuccessMessage('Content copied to clipboard (plain text only)');
            })
            .catch(fallbackErr => {
              console.error('Clipboard fallback also failed: ', fallbackErr);
              showErrorMessage(errorMessage);
            });
        });
    } catch (err) {
      console.error('Error in copy process: ', err);
      showErrorMessage(errorMessage);
    }
  }

export const evaluateCondition = (condition, record) => {
  if(!condition) return false;
  return typeof condition === 'function' ? condition(record) : condition;
};

export const getFileName = (fullFileName) => {
  if(!fullFileName) return fullFileName;
  let index = fullFileName.lastIndexOf('/');
  if(index < 0) return fullFileName;
  return fullFileName.substr(index + 1);
}

export const ensureHTTPS = (url) => {
  if(!url) return url;
  
  if (url.startsWith('https://')) {
    return url;
  }
  else if (url.startsWith('http://')) {
    return url.replace('http://', 'https://');
  }
  else {
    if(url.indexOf('://') > 0) {
      return url;
    }
    else if (url.indexOf('://') === 0) {
      return 'https' + url;
    }
    return 'https://' + url;
  }
}

export const objectsAreDifferent = (obj1, obj2) => {
  if (typeof obj1 !== typeof obj2) {
    return true;
  }

  if (obj1 === obj2) {
    return false;
  }

  if (typeof obj1 !== 'object' || obj1 === null || obj2 === null) {
    return obj1 !== obj2;
  }

  // Handle arrays - compare by content, not order
  const isArray1 = Array.isArray(obj1);
  const isArray2 = Array.isArray(obj2);
  
  if (isArray1 !== isArray2) {
    return true;
  }
  
  if (isArray1) {
    if (obj1.length !== obj2.length) {
      return true;
    }
    
    const matched = new Array(obj2.length).fill(false);
    
    for (let i = 0; i < obj1.length; i++) {
      let found = false;
      for (let j = 0; j < obj2.length; j++) {
        if (!matched[j] && !objectsAreDifferent(obj1[i], obj2[j])) {
          matched[j] = true;
          found = true;
          break;
        }
      }
      if (!found) {
        return true;
      }
    }
    
    return false;
  }

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) {
    return true;
  }

  for (const key of keys1) {
    if (!keys2.includes(key) || objectsAreDifferent(obj1[key], obj2[key])) {
      return true;
    }
  }

  return false;
}

export const getBusinessInitials = (name, maxLetters = 2) => {
  const words = String(name ?? '')
    .replace(/['’]/g, '')
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  return words
    .slice(0, maxLetters)
    .map((word) => word[0].toUpperCase())
    .join('');
};

export const separateWords = (inputString) => {
  if (!inputString) {
    return "";
  }

  let result = inputString.charAt(0);
  for (let i = 1; i < inputString.length; i++) {
    if (inputString.charAt(i) === inputString.charAt(i).toUpperCase()) {
      result += " ";
    }
    result += inputString.charAt(i);
  }
  return result;
}

/**
 * FIXME - Improve the function name such that it tells what it does.
 * @param {string} word 
 * @param {string} substring 
 * @returns 
 */

export function getGeneratedContentKey(entityName, userId, recordId) {
  return `${entityName}-${userId}-${recordId}`;
};

/**
 * Returns a string containing a color code to be used in SEO Guage based on the
 * given `percentage` value. The returned value is intended to be used as CSS
 * attribute value.
 * @param {number} percentage The value corresponding to which a color code is
 * to be returned.
 * @returns 
 */
export const getSEOGuageColor = (percentage) => {
  if (percentage <= 12.5) return '#ED2A24'
  if (percentage <= 25) return '#F15323'
  if (percentage <= 37.5) return '#FF7D55'
  if (percentage <= 50) return '#F8981C'
  if (percentage <= 62.5) return '#FCE917'
  if (percentage <= 75) return '#C4DB26'
  if (percentage <= 87.5) return '#90DB26'
  if (percentage <= 100) return '#39AD40'
  return '#f1f1f1'
}

/**
 * Returns a string containing a color code to be used as the text color in SEO
 * keyword UI widgets based on the given `percentage` value. The returned value
 * is intended to be used as CSS attribute value.
 * @param {number} percentage The value corresponding to which a color code is
 * to be returned.
 * @returns 
 */

export const clampValueToMaxLength = (value, maxLength, skipClamp = false) => {
  if (skipClamp) {
    return typeof value === 'string' ? value : (value ?? '');
  }
  if (typeof value !== 'string') {
    return value ?? '';
  }
  if (!maxLength) {
    return value;
  }
  return value.length > maxLength ? value.slice(0, maxLength) : value;
};

export const getContentHistoryFromLocalStorage = (entityName, userId, contentId) => { 
  if (!userId || !contentId) return [];
  const key = getGeneratedContentKey(entityName, userId, contentId);
  try {
    const storedHistory = JSON.parse(localStorage.getItem(key));
    return storedHistory ?? [];
  } catch (error) {
    console.error("Error reading content history from localStorage:", error);
    return [];
  }
};

export const saveGeneratedContentsToLocalStorage = (entityName, userId, contentId, generatedContents) => { 
  console.log("Saving content to localStorage:", generatedContents, userId, contentId);
  if (!userId || !contentId || !Array.isArray(generatedContents)) return; // Allow saving empty array to clear

  const key = getGeneratedContentKey(entityName, userId, contentId);
  localStorage.setItem(key, JSON.stringify(generatedContents));
};

export const getUserReadableError = (error) => {
  return !!error?.response?.data?.isForUser ? error.response.data?.message : null
}

export const isUserUnauthorized = (errorObj) => {
  return errorObj?.response?.status === 401;
}

export const handleUserIsUnauthorized = (errorObj, returnObj) => {
  if (isUserUnauthorized(errorObj)) return returnObj;
  throw errorObj;
}

export const handleAPIError = (errorObj, defaultMessage) => {
  if (isUserUnauthorized(errorObj)) return; // Return quietly as it has been handled by apiUtils

  console.error(errorObj);

  let errorMessage = defaultMessage ?? errorObj?.message ?? (typeof errorObj === 'string' ? errorObj : 'Unknown error occurred');
  if(!!errorObj?.response?.data?.isForUser && errorObj.response.data?.message) {
    errorMessage = errorObj.response.data.message
  }
  showErrorMessage(errorMessage);
}

/**
 * Converts an ISO 8601 duration string (e.g., "PT1H23M45S")
 * into a human-readable format (e.g., "1 hour 23 minutes 45 seconds").
 *
 * @param {string} isoDuration The ISO 8601 duration string.
 * @returns {string} The human-readable duration string, or an empty string if parsing fails.
 */

/**
 * Formats an ISO 8601 duration string (e.g., "PT1H23M45S")
 * into a standard time format (e.g., 00:34 or 21:56 or 01:09:05).
 *
 * @param {string} isoDuration The ISO 8601 duration string.
 * @returns {string} The standard time string, or an empty string if parsing fails.
 */

/**
 * Converts an ISO 8601 duration string (e.g., "PT1H23M45S")
 * into a seconds.
 *
 * @param {string} isoDuration The ISO 8601 duration string.
 * @returns {number} Number of seconds.
 */
export const convertISODurationToSeconds = (isoDuration) => {
  if (!isoDuration || typeof isoDuration !== 'string') {
    return 0;
  }

  const parts = isoDuration.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);

  if (!parts) {
    return 0;
  }

  let seconds = parseInt(parts[3] ?? 0);
  seconds += !!parts[2] ? parseInt(parts[2]) * 60 : 0;
  seconds += !!parts[1] ? parseInt(parts[1]) * 3600 : 0;
  return seconds;
}



// Utility to unwrap content from <code class="language-html">...</code>
export const unwrapHtmlContent = (htmlString) => {
  if (typeof htmlString !== 'string' || !htmlString.includes('<code')) {
    return htmlString;
  }
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, 'text/html');
    const codeElement = doc.querySelector('code.language-html');
    if (codeElement) {
      return decodeHtmlEntities(codeElement.textContent);
    }
  } catch (error) {
    console.error("Error parsing HTML string:", error);
  }
  return htmlString;
};

export const filterHtmlCodeBlocks = (htmlString) => {
  if (typeof htmlString !== 'string') {
    return htmlString;
  }
  try {
    // Unwrap content from <pre><code>...</code></pre> blocks, keeping and decoding the content.
    let result = htmlString.replace(/<pre><code[\s\S]*?>([\s\S]*?)<\/code><\/pre>/gi, (match, p1) => decodeHtmlEntities(p1)).trim();
    // Also unwrap content from any standalone <code>...</code> blocks that might not be in a <pre>
    result = result.replace(/<code[\s\S]*?>([\s\S]*?)<\/code>/gi, (match, p1) => decodeHtmlEntities(p1)).trim();
    return result;
  } catch (error) {
    console.error("Error filtering HTML code blocks:", error);
  }
  return htmlString;
};

/**
 * Sanitizes content by removing specific HTML tags and their content
 * that should not be included in AI processing.
 * @param {string} content - The HTML content to sanitize
 * @returns {string} - The sanitized content
 */
export const sanitizeContent = (content) => {
  if (!content || typeof content !== 'string') {
    return '';
  }
  
  return content
    .replace(/<img[^>]*>/gi, '')
    // Remove hidden content blocks WITH their inner content
    .replace(/<tldr[^>]*>[\s\S]*?<\/tldr>/gi, '')
    .replace(/<faq[^>]*>[\s\S]*?<\/faq>/gi, '')
    .replace(/<testimonial[^>]*>[\s\S]*?<\/testimonial>/gi, '')
    .replace(/<textsuggestionpanel[^>]*>[\s\S]*?<\/textsuggestionpanel>/gi, '')
    .replace(/<authorbio[^>]*>[\s\S]*?<\/authorbio>/gi, '')
    .replace(/<lastupdated[^>]*>[\s\S]*?<\/lastupdated>/gi, '')
    // Fallback: also strip any remaining orphaned opening/closing tags
    .replace(/<\/?tldr[^>]*>/gi, '')
    .replace(/<\/?faq[^>]*>/gi, '')
    .replace(/<\/?testimonial[^>]*>/gi, '')
    .replace(/<\/?textsuggestionpanel[^>]*>/gi, '')
    .replace(/<\/?authorbio[^>]*>/gi, '')
    .replace(/<\/?lastupdated[^>]*>/gi, '');
};
